"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { apiGet } from "@/lib/api";
import { $n } from "@/lib/format";
import { ChartIcon } from "@/lib/icons";
import { ChartModal } from "@/components/chart-modal";
import {
  MARKET_INDICES as INDICES,
  indexChangeColor,
  indexGap,
  indexGapTitle,
  useMarketIndices,
} from "@/lib/use-market-indices";
import { accountNickname } from "@/lib/account-name";
import { countOpenTrades } from "@/lib/strategy-open-counts";
import { fetchAccounts, type UiAccount } from "@/lib/api";
import { BrokerLogo } from "@/app/strategies/components/strategy-page-header";
import { fmtMDY } from "@/app/trades/summary-columns";
import {
  ACCOUNT_RETURNS_CHANGED, fetchAccountStart, fetchReturnsWindows, fetchTotalReturn, resolveSince,
  signedPct1, tone,
} from "@/lib/account-returns";

// dir = config route segment; flag = account_strategy_flags strategy key.
const STRATEGIES: { dir: string; label: string; flag: string }[] = [
  { dir: "covered-calls", label: "Covered Calls", flag: "covered_calls" },
  { dir: "vertical-spreads", label: "Vertical Index Spreads", flag: "vertical_spreads" },
  { dir: "0dte-vertical-spreads", label: "0DTE Vertical Index Spreads", flag: "vertical_spreads_0dte" },
  { dir: "equity-vertical-spreads", label: "Vertical Stock Spreads", flag: "vertical_spreads_equity" },
  { dir: "stock-trading", label: "Stock Trading", flag: "stock_trading" },
];


export default function MarketIndices() {
  const { user } = useAuth();
  const pathname = usePathname();
  const { marketData, mode, polling, error } = useMarketIndices();
  const [chartSymbol, setChartSymbol] = useState<string | null>(null);
  // Per-account strategy links shown below the indices.
  const [accts, setAccts] = useState<Record<string, UiAccount>>({});
  const [flags, setFlags] = useState<{ accountHash: string; strategy: string; enabled: boolean }[]>([]);
  // Accounts section: total return since each account's "Returns since" date,
  // the same number as the Accounts page's Total return column.
  const [acctReturns, setAcctReturns] = useState<
    { hash: string; account: UiAccount; name: string; since: string | null; ret: number | null; loading: boolean }[]
  >([]);

  const loadAccountReturns = useCallback(() => {
    if (!user) return;
    Promise.all([fetchAccounts(), fetchReturnsWindows()])
      .then(async ([accounts, byHash]) => {
        const starts = await Promise.all(
          accounts.map((a) => fetchAccountStart(a.hashValue).catch(() => "")),
        );
        const base = accounts.map((a, i) => {
          const { since } = resolveSince(starts[i], byHash.get(a.hashValue));
          return { hash: a.hashValue, account: a, name: accountNickname(a), since, ret: null as number | null, loading: !!since };
        });
        setAcctReturns(base);
        // Fill each account independently so one slow or failing call never blanks the list.
        for (const r of base) {
          if (!r.since) continue;
          fetchTotalReturn(r.hash, r.since)
            .then((ret) => {
              setAcctReturns((prev) => prev.map((p) => (p.hash === r.hash ? { ...p, ret, loading: false } : p)));
            })
            .catch(() => {
              setAcctReturns((prev) => prev.map((p) => (p.hash === r.hash ? { ...p, loading: false } : p)));
            });
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    loadAccountReturns();
    // Refresh when a "Returns since" date is saved on the Accounts page, and
    // every five minutes so the day's closed trades show up.
    const onChange = () => loadAccountReturns();
    window.addEventListener(ACCOUNT_RETURNS_CHANGED, onChange);
    const id = setInterval(() => { if (!document.hidden) loadAccountReturns(); }, 5 * 60_000);
    return () => {
      window.removeEventListener(ACCOUNT_RETURNS_CHANGED, onChange);
      clearInterval(id);
    };
  }, [loadAccountReturns]);

  const loadStrategyLinks = useCallback(() => {
    if (!user) return;
    fetchAccounts()
      .then((rows) => {
        const m: Record<string, UiAccount> = {};
        for (const a of rows) m[a.hashValue] = a;
        setAccts(m);
      })
      .catch(() => {});
    apiGet<{ accountHash: string; strategy: string; enabled: boolean }[]>("/strategies/account-flags")
      .then(setFlags)
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    loadStrategyLinks();
    // Refresh when a strategy tag is toggled on the Accounts page.
    const onChange = () => loadStrategyLinks();
    window.addEventListener("strategy-flags-changed", onChange);
    return () => window.removeEventListener("strategy-flags-changed", onChange);
  }, [loadStrategyLinks]);

  // "n open" at the right of each account link: open trades on that strategy page,
  // keyed `${dir}|${hash}`. Each spread count costs a broker fill check on the
  // backend, so load them one at a time and refresh only every five minutes.
  const [openCounts, setOpenCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!user) return;
    const pairs = STRATEGIES.flatMap((s) =>
      flags.filter((f) => f.strategy === s.flag && f.enabled).map((f) => ({ dir: s.dir, hash: f.accountHash })),
    );
    // Wait for accounts too: Stock Trading counts need the login token id.
    if (pairs.length === 0 || Object.keys(accts).length === 0) return;
    let cancelled = false;
    const load = async () => {
      const indices = new Set(await apiGet<string[]>("/prices/index-underlyings").catch(() => [] as string[]));
      for (const p of pairs) {
        if (cancelled) return;
        try {
          const n = await countOpenTrades(p.dir, p.hash, indices, accts[p.hash]?.tokenId);
          if (cancelled) return;
          const k = `${p.dir}|${p.hash}`;
          setOpenCounts((prev) => (prev[k] === n ? prev : { ...prev, [k]: n }));
        } catch { /* keep the last count */ }
      }
    };
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 5 * 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, flags, accts]);

  // 5-minute bars need an account hash to pick a broker token. This pane is
  // not tied to the main page, so don't use `?account=` — pick an account
  // actually shown under Strategies below (not "hidden" flags). Prefer
  // Schwab: cash-index 5-minute bars come from Schwab pricehistory; paper
  // borrows that feed; tastytrade's index candle feed is often empty.
  const chartAccountHash = useMemo(() => {
    const hashes = [...new Set(
      STRATEGIES.flatMap((s) =>
        flags.filter((f) => f.strategy === s.flag && f.enabled).map((f) => f.accountHash),
      ),
    )];
    return hashes.find((h) => !h.startsWith("PAPER:") && !h.startsWith("TDA:") && !h.startsWith("tastytrade:"))
      ?? hashes.find((h) => h.startsWith("PAPER:"))
      ?? hashes.find((h) => h.startsWith("tastytrade:"))
      ?? hashes[0]
      ?? null;
  }, [flags]);

  // The Documentation and User Guide pages are full-width — no Indices pane there.
  if (!user || pathname.startsWith("/documentation") || pathname.startsWith("/user-guide")) return null;

  return (
    <aside className="fixed left-0 top-[50px] bottom-[50px] w-[230px] bg-surface border-r border-border z-30 overflow-y-auto flex flex-col">
      <h2
        className="text-xs font-semibold text-amber py-1.5 px-3 bg-amber/10 border-b border-border flex items-center justify-between"
        title={
          mode === "futures"
            ? "Overnight futures vs most recent completed cash session close (today after 4pm ET)"
            : "Cash index quotes"
        }
      >
        <span>
          {mode === "futures" ? (
            <>
              Indices <span className="font-normal">(Futures)</span>
            </>
          ) : (
            "Indices"
          )}
        </span>
        <span
          className={`w-1.5 h-1.5 rounded-full shrink-0 ${error ? "bg-red" : polling ? "bg-amber" : "bg-green"}`}
          title={
            error
              ? error
              : polling
                ? "Live stream is down. Prices are from a 5-second quotes poll."
                : "Live stream is up."
          }
        />
      </h2>
      <table className="w-full border-collapse text-xs font-mono tabular-nums">
        <tbody>
          {INDICES.map(({ symbol, label }) => {
            const d = marketData[symbol];
            const g = indexGap(d);
            const color = indexChangeColor(d?.latest ?? 0, d?.prev);
            const sign = g ? (g.chg > 0 ? "+" : g.chg < 0 ? "-" : "") : "";
            return (
              <tr
                key={symbol}
                title={indexGapTitle(label, mode, d)}
                className="border-b border-border"
              >
                <td className={`pl-2 pr-1 py-1 whitespace-nowrap ${mode === "futures" ? "text-amber" : "text-muted"}`}>
                  {label}
                </td>
                <td className="px-0.5 py-1 w-[16px]">
                  <button
                    onClick={() => setChartSymbol(symbol)}
                    title={`${label} price chart`}
                    className="inline-flex items-center justify-center p-0 m-0 leading-none text-muted hover:text-cyan transition-colors"
                  >
                    <ChartIcon />
                  </button>
                </td>
                <td className={`px-1 py-1 text-right whitespace-nowrap ${d ? color : "text-muted"}`}>
                  {d ? $n(d.latest) : "—"}
                </td>
                <td className={`px-1 py-1 text-right whitespace-nowrap ${color}`}>
                  {g ? `${sign}${$n(Math.abs(g.chg))}` : ""}
                </td>
                <td className={`pl-1 pr-2 py-1 text-right whitespace-nowrap font-semibold ${color}`}>
                  {g ? `${sign}${Math.abs(g.pct).toFixed(2)}%` : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Strategies → accounts: a link to each account's per-account config. */}
      <h2 className="text-xs font-semibold text-amber py-1.5 px-3 bg-amber/10 border-t border-border">
        Strategies
      </h2>
      <div className="flex flex-col px-2 pb-2">
        {(() => {
          const enabled = STRATEGIES.map((s) => ({
            ...s,
            accts: flags
              .filter((f) => f.strategy === s.flag && f.enabled)
              .map((f) => f.accountHash),
          })).filter((s) => s.accts.length > 0);
          if (enabled.length === 0) {
            return (
              <p className="px-1 py-0.5 text-[11px] text-muted leading-snug">
                None enabled.{" "}
                <Link
                  href="/accounts"
                  className="text-accent hover:text-cyan underline"
                >
                  Accounts
                </Link>
                {" "}— turn on VIS, 0VIS, VSS, or CC for an account.
              </p>
            );
          }
          return enabled.map((s) => (
            <div key={s.dir} className="mb-1.5">
              <Link
                href={`/strategies/${s.dir}`}
                className="block px-1 py-0.5 text-xs font-medium text-amber hover:text-cyan no-underline"
              >
                {s.label}
              </Link>
              {s.accts.map((h) => (
                // Plain <a> (hard navigation), NOT next/link: switching
                // accounts only changes the ?account= query within the same
                // route, which Next soft-navigates WITHOUT remounting the
                // page — so the data hook's in-flight account fetch races and
                // can leave stale/empty positions. A full page load mounts the
                // hook fresh against the chosen account.
                <a
                  key={h}
                  href={`/strategies/${s.dir}?account=${encodeURIComponent(h)}`}
                  className="flex items-center gap-1.5 pl-3 pr-1 py-0.5 text-[11px] text-muted hover:text-cyan no-underline"
                  title={accts[h] ? accountNickname(accts[h]) : h}
                >
                  {accts[h] && <BrokerLogo account={accts[h]} className="h-3.5 w-3.5 object-contain rounded shrink-0" />}
                  <span className="truncate">{accts[h] ? accountNickname(accts[h]) : h}</span>
                  {openCounts[`${s.dir}|${h}`] != null && (
                    <span className="ml-auto shrink-0 whitespace-nowrap">{openCounts[`${s.dir}|${h}`]} open</span>
                  )}
                </a>
              ))}
            </div>
          ));
        })()}
      </div>

      {/* Accounts → total return since each account's "Returns since" date.
          Pinned to the bottom of the sidebar (mt-auto on the flex column). */}
      <h2 className="mt-auto text-xs font-semibold text-amber py-1.5 px-3 bg-amber/10 border-t border-border">
        Accounts
      </h2>
      <table className="w-full border-collapse text-xs font-mono tabular-nums">
        <tbody>
          {acctReturns.map((r) => (
            <tr
              key={r.hash}
              className="border-b border-border"
              title={
                r.since
                  ? `Time-weighted return since ${fmtMDY(r.since)}, measured on the account’s reconstructed balance with deposits and withdrawals taken out.`
                  : "No trades yet."
              }
            >
              <td className="pl-2 pr-1 py-1 whitespace-nowrap max-w-[150px]">
                <Link href="/accounts" className="flex items-center gap-1.5 text-muted hover:text-cyan no-underline">
                  <BrokerLogo account={r.account} className="h-3.5 w-3.5 object-contain rounded shrink-0" />
                  <span className="truncate">{r.name}</span>
                </Link>
              </td>
              <td className={`pl-1 pr-2 py-1 text-right whitespace-nowrap font-semibold ${r.ret != null ? tone(r.ret) : "text-muted"}`}>
                {r.loading ? "…" : r.ret != null ? signedPct1(r.ret) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ChartModal symbol={chartSymbol} onClose={() => setChartSymbol(null)} accountHash={chartAccountHash} />
    </aside>
  );
}
